import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  API_CREATE_MEETING_INFO,
  API_GET_ALL_MEETING_INFO,
  API_GET_ONE_MEETING_INFO,
  API_UPDATE_MEETING_INFO,
  API_DELETE_MEETING_INFO,
  API_CANCEL_MEETING,
} from "../api/meeting.service";

const initialState = {
  meetings: [],
  currentMeeting: null,
  isLoading: false,
  error: null,
  page: 0,
  limit: 30,
  count: 0,
  mode: "create",
};

export const createMeetingInfo = createAsyncThunk(
  "meeting/createMeetingInfo",
  async (data, thunkAPI) => {
    try {
      const response = await API_CREATE_MEETING_INFO(data);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const getAllMeetingInfo = createAsyncThunk(
  "meeting/getAllMeetingInfo",
  async (
    { page, limit, status, global, startTimestamp, endTimestamp } = {},
    thunkAPI
  ) => {
    try {
      const response = await API_GET_ALL_MEETING_INFO({
        page,
        limit,
        status,
        global,
        startTimestamp,
        endTimestamp,
      });
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const getOneMeetingInfo = createAsyncThunk(
  "meeting/getOneMeetingInfo/:id",
  async (id, thunkAPI) => {
    try {
      const response = await API_GET_ONE_MEETING_INFO(id);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const updateMeetingInfo = createAsyncThunk(
  "meeting/updateMeetingInfo",
  async ({ id, data }, thunkAPI) => {
    try {
      const response = await API_UPDATE_MEETING_INFO(id, data);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const deleteMeetingInfo = createAsyncThunk(
  "meeting/deleteMeetingInfo",
  async (id, thunkAPI) => {
    try {
      const response = await API_DELETE_MEETING_INFO(id);
      return id;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);
export const cancelMeetingInfo = createAsyncThunk(
  "meeting/cancel",
  async (id, thunkAPI) => {
    try {
      await API_CANCEL_MEETING(id);
      return id; // Return the ID of the cancelled meeting
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

const meetingSlice = createSlice({
  name: "meeting",
  initialState,
  reducers: {
    resetMeetingError(state) {
      state.error = null;
    },
    clearCurrentMeeting(state) {
      state.currentMeeting = null;
    },
    changePage(state, action) {
      state.page = action.payload;
    },
    changelimit(state, action) {
      state.limit = action.payload;
    },
    resetMeetingList(state) {
      state.page = initialState.page;
      state.limit = initialState.limit;
      state.count = initialState.count;
      state.meetings = initialState.meetings;
    },
    changeMode(state, action) {
      state.mode = action.payload;
    },

    resetMode(state) {
      state.mode = initialState.mode;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createMeetingInfo.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(createMeetingInfo.fulfilled, (state, action) => {
      state.isLoading = false;
      state.meetings.push(action.payload);
    });
    builder.addCase(createMeetingInfo.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || action.error.message;
    });

    builder.addCase(getAllMeetingInfo.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getAllMeetingInfo.fulfilled, (state, action) => {
      state.isLoading = false;
      state.meetings = action.payload.rows;
      state.count = action.payload.count;
    });
    builder.addCase(getAllMeetingInfo.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || action.error.message;
    });

    builder.addCase(getOneMeetingInfo.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getOneMeetingInfo.fulfilled, (state, action) => {
      state.isLoading = false;
      state.currentMeeting = action.payload;
    });
    builder.addCase(getOneMeetingInfo.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || action.error.message;
    });

    builder.addCase(updateMeetingInfo.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(updateMeetingInfo.fulfilled, (state, action) => {
      state.isLoading = false;
      const updatedMeeting = action.payload;
      state.meetings = state.meetings.map((meeting) =>
        meeting.id === updatedMeeting.id ? updatedMeeting : meeting
      );
      if (
        state.currentMeeting &&
        state.currentMeeting.id === updatedMeeting.id
      ) {
        state.currentMeeting = updatedMeeting;
      }
    });
    builder.addCase(updateMeetingInfo.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || action.error.message;
    });

    builder.addCase(deleteMeetingInfo.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(deleteMeetingInfo.fulfilled, (state, action) => {
      state.isLoading = false;
      const deletedId = action.payload;
      state.meetings = state.meetings.filter(
        (meeting) => meeting.id !== deletedId
      );
      if (state.currentMeeting && state.currentMeeting.id === deletedId) {
        state.currentMeeting = null;
      }
    });
    builder.addCase(deleteMeetingInfo.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || action.error.message;
    });

    // --- ADD THESE NEW CASES FOR CANCELLATION ---
    builder.addCase(cancelMeetingInfo.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(cancelMeetingInfo.fulfilled, (state, action) => {
      state.isLoading = false;
      const cancelledId = action.payload;
      // Find the meeting in the list and update its status
      const meetingToUpdate = state.meetings.find((m) => m.id === cancelledId);
      if (meetingToUpdate) {
        meetingToUpdate.status = "cancelled";
      }
      // Also update the current meeting if it's the one being viewed
      if (state.currentMeeting && state.currentMeeting.id === cancelledId) {
        state.currentMeeting.status = "cancelled";
      }
    });
    builder.addCase(cancelMeetingInfo.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || action.error.message;
    });
  },
});

export const {
  resetMeetingError,
  clearCurrentMeeting,
  changeMode,
  changePage,
  changelimit,
  resetMeetingList,
  resetMode,
} = meetingSlice.actions;
export default meetingSlice.reducer;
